/**
 * Purchase calculations + dashboard derivations.
 *
 * SPEC §4.1, §4.2 and the Dashboard cells (M45, F45, D45, G45, J4, M36, C43, D43).
 */

import { Decimal, ZERO, ONE, btcToSats, sum } from './money.js';
import type { Btc, Sats, Usd } from './money.js';
import type { PurchaseTransaction } from './types.js';

// ---------- per-row helpers ----------

/** Purchase Log!E: effective purchase price = USD / BTC. Returns null when BTC = 0. */
export function effectivePurchasePrice(usd: Usd, btc: Btc): Usd | null {
  if (btc.isZero()) return null;
  if (usd.lessThanOrEqualTo(0)) return null; // matches workbook IF(C>0, …, "")
  return usd.div(btc);
}

// ---------- aggregate helpers (operate on a sorted list) ----------

/** Sort transactions by date in-place-safe (returns a new sorted array). */
export function sortByDate<T extends { date: Date }>(txs: T[]): T[] {
  return [...txs].sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Running BTC balance after each transaction. Workbook Purchase Log!F:
 *   F6 = D6,  F_n = D_n + F_{n-1}.
 */
export function runningBtcBalance(txs: PurchaseTransaction[]): Btc[] {
  let acc = ZERO;
  return txs.map(t => {
    acc = acc.plus(t.btc);
    return acc;
  });
}

/** Running cumulative USD invested. Workbook SUM($C$6:Cn). */
export function cumulativeUsdInvested(txs: PurchaseTransaction[]): Usd[] {
  let acc = ZERO;
  return txs.map(t => {
    acc = acc.plus(t.usd);
    return acc;
  });
}

/**
 * Weighted-average cost basis: cumulative USD / running balance. Workbook G:
 *   G_n = SUM($C$6:Cn) / F_n.
 * Returns ZERO at any step where balance is zero (no purchases yet).
 */
export function weightedAverageCostBasis(txs: PurchaseTransaction[]): Usd[] {
  const balances = runningBtcBalance(txs);
  const cumUsd = cumulativeUsdInvested(txs);
  return balances.map((bal, i) => (bal.isZero() ? ZERO : cumUsd[i]!.div(bal)));
}

// ---------- dashboard scalars ----------

/** Final BTC balance from the transaction list. */
export function totalBtcBalance(txs: PurchaseTransaction[]): Btc {
  return sum(txs.map(t => t.btc));
}

/** Total stack in sats (bigint). Workbook M45 = MAX(F6:F191) * 1e8. */
export function stackSatoshis(txs: PurchaseTransaction[]): Sats {
  return btcToSats(totalBtcBalance(txs));
}

/** Stack USD value at a given current BTC price. Workbook F45 = balance * X6. */
export function stackUsdValue(btc: Btc, currentPriceUsd: Usd): Usd {
  return btc.times(currentPriceUsd);
}

/** Cumulative USD invested. Workbook SUM('Purchase Log'!C6:C…). */
export function totalUsdInvested(txs: PurchaseTransaction[]): Usd {
  return sum(txs.map(t => t.usd));
}

/** Unrealized P/L in USD. Workbook D45 = F45 - SUM(C). */
export function unrealizedGainLoss(currentValue: Usd, cumulativeCost: Usd): Usd {
  return currentValue.minus(cumulativeCost);
}

/**
 * Unrealized P/L %.
 * - 'cost'  (default per Q5): gain / cumulativeCost  -  standard convention.
 * - 'value' (workbook G45):    gain / currentValue.
 * Returns ZERO if denominator is zero.
 */
export function unrealizedGainLossPercent(
  gain: Usd,
  cumulativeCost: Usd,
  currentValue: Usd,
  denominator: 'cost' | 'value' = 'cost',
): Decimal {
  const denom = denominator === 'cost' ? cumulativeCost : currentValue;
  if (denom.isZero()) return ZERO;
  return gain.div(denom);
}

/** Goal progress as a fraction 0..1. Workbook M36 / J4. */
export function progressToGoal(currentBtc: Btc, targetBtc: Btc): Decimal {
  if (targetBtc.isZero()) return ZERO;
  return currentBtc.div(targetBtc);
}

// ---------- estimated time-to-goal ----------

export type PaceMethod = 'allTimePace' | 'recentPace';

export interface TimeToGoalInput {
  txs: PurchaseTransaction[];
  targetBtc: Btc;
  asOf: Date;
  method: PaceMethod;
  /** For 'recentPace'  -  window in days. Default 90. */
  recentDays?: number;
}

/**
 * Estimated date a goal will be reached based on stacking pace.
 *
 * - 'allTimePace' replicates the workbook (Dashboard!C43 / D43):
 *     date = firstPurchase + (today − firstPurchase) * targetBtc / currentBtc
 *
 * - 'recentPace' uses the average BTC/day over the last N days and projects forward:
 *     remaining = target − current; date = today + remaining / paceBtcPerDay
 *
 * Returns null when not yet projectable (no purchases, target already met, or zero pace).
 */
export function estimatedDateToGoal(input: TimeToGoalInput): Date | null {
  const { targetBtc, asOf, method } = input;
  const txs = sortByDate(input.txs);
  if (txs.length === 0) return null;

  const currentBtc = totalBtcBalance(txs);
  if (currentBtc.greaterThanOrEqualTo(targetBtc)) return null;

  if (method === 'allTimePace') {
    const first = txs[0]!.date;
    const elapsedMs = asOf.getTime() - first.getTime();
    if (elapsedMs <= 0 || currentBtc.isZero()) return null;
    // ms_to_target = elapsed * target / current  => date = first + ms_to_target
    const totalMs = new Decimal(elapsedMs).times(targetBtc).div(currentBtc);
    return new Date(first.getTime() + totalMs.toNumber());
  }

  // recentPace
  const windowDays = input.recentDays ?? 90;
  const windowStart = new Date(asOf.getTime() - windowDays * 86_400_000);
  const recent = txs.filter(t => t.date.getTime() >= windowStart.getTime());
  if (recent.length === 0) return null;
  const recentBtc = sum(recent.map(t => t.btc));
  if (recentBtc.isZero()) return null;
  const paceBtcPerDay = recentBtc.div(windowDays);
  const remainingBtc = targetBtc.minus(currentBtc);
  const daysOut = remainingBtc.div(paceBtcPerDay);
  return new Date(asOf.getTime() + daysOut.times(86_400_000).toNumber());
}

/** Format a future date as "X years, Y months, Z days" relative to `from`. */
export function formatTimeToGoal(from: Date, to: Date | null): string {
  if (to === null) return '-';
  if (to.getTime() <= from.getTime()) return 'Reached';

  // Calendar-aware diff (matches Excel DATEDIF semantics).
  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let days = to.getDate() - from.getDate();

  if (days < 0) {
    months -= 1;
    // days in the month preceding `to`
    const prevMonth = new Date(to.getFullYear(), to.getMonth(), 0);
    days += prevMonth.getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return `${years} years, ${months} months, ${days} days`;
}
